import { useState } from "react";
import { Filter, MapPin, Pencil, Save, X } from "lucide-react";
import { Spinner } from "../components/Spinner.tsx";
import { db } from "../db.ts";
import { tx } from "@instantdb/react";
import { cats } from "../../../src/category.ts";

type Country = "Israel" | "US" | "Germany" | "Argentina";
type SubType = "" | "group" | "single";

const countries: { value: Country | ""; label: string }[] = [
  { value: "", label: "All countries" },
  { value: "Israel", label: "Israel" },
  { value: "US", label: "US" },
  { value: "Germany", label: "Germany" },
  { value: "Argentina", label: "Argentina" },
];

const subTypes: { value: SubType; label: string }[] = [
  { value: "", label: "All types" },
  { value: "group", label: "Group" },
  { value: "single", label: "Single" },
];

const allCategories = Object.keys(cats);

const maskPhone = (phone: string) =>
  phone.length <= 6 ? phone : phone.slice(0, 3) + "***" + phone.slice(-3);

const formatDate = (ts?: number) =>
  ts
    ? new Date(ts).toLocaleString("en-US", {
      dateStyle: "short",
      timeStyle: "short",
    })
    : "-";

type Sub = {
  id: string;
  phone: string;
  country: string;
  categories: string[];
  blacklistedCategories?: string[];
  location?: string;
  radiusKm?: number;
  pushRRule?: string;
  pushLocalHour?: number;
  pushLocalMinute?: number;
  ticketAlerts?: string;
  whitelistedWords?: string[];
  blacklistedWords?: string[];
  waitingEventIds?: string[];
  waitingTicketTradeIds?: string[];
  lastAggregatedPushAt?: number;
  conversationId?: string;
};

const scheduleLabel = (sub: Sub) => {
  const rrule = sub.pushRRule ?? "FREQ=DAILY";
  const hour = sub.pushLocalHour ?? 18;
  const minute = sub.pushLocalMinute ?? 0;
  const isFrequent = rrule.startsWith("FREQ=MINUTELY") ||
    rrule.startsWith("FREQ=HOURLY");
  return isFrequent
    ? rrule
    : `${rrule} at ${hour}:${String(minute).padStart(2, "0")}`;
};

const activeClassByTone = {
  positive: "bg-purple-600 border-purple-500 text-white",
  negative: "bg-red-600 border-red-500 text-white",
};

const CategoryEditor = (
  { categories, tone, onSave }: {
    categories: string[];
    tone: "positive" | "negative";
    onSave: (cats: string[]) => void;
  },
) => {
  const [selected, setSelected] = useState<Set<string>>(
    new Set(categories),
  );

  const toggle = (cat: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1 max-h-40 overflow-y-auto">
        {allCategories.map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => toggle(cat)}
            className={`px-2 py-0.5 text-xs rounded-full border ${
              selected.has(cat)
                ? activeClassByTone[tone]
                : "bg-gray-800 border-gray-600 text-gray-400"
            }`}
          >
            {cat}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={() => onSave([...selected])}
        className="flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300"
      >
        <Save size={12} /> Save categories
      </button>
    </div>
  );
};

const BlacklistEditor = (
  { words, onSave }: { words: string[]; onSave: (w: string[]) => void },
) => {
  const [text, setText] = useState(words.join(", "));
  return (
    <div className="flex items-center gap-2">
      <Input
        size="sm"
        value={text}
        onChange={(e) =>
          setText((e as unknown as { target: { value: string } }).target.value)}
        className="flex-1"
        placeholder="comma separated words"
      />
      <button
        type="button"
        onClick={() =>
          onSave(
            text.split(",").map((w) => w.trim()).filter(Boolean),
          )}
        className="flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300"
      >
        <Save size={12} />
      </button>
    </div>
  );
};

const SubscriptionCard = (
  { sub, groupName, onUpdate }: {
    sub: Sub;
    groupName?: string;
    onUpdate: (id: string, fields: Record<string, unknown>) => void;
  },
) => {
  const [editing, setEditing] = useState<
    "categories" | "blockedCategories" | "blacklist" | null
  >(null);

  const toggleEditing = (
    target: "categories" | "blockedCategories" | "blacklist",
  ) => setEditing(editing === target ? null : target);

  return (
    <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 space-y-2">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-white font-mono text-sm">
              {maskPhone(sub.phone)}
            </span>
            <span className="px-2 py-0.5 text-xs rounded bg-gray-700 text-gray-300">
              {sub.country}
            </span>
            {sub.conversationId && (
              <span className="px-2 py-0.5 text-xs rounded bg-purple-900/50 text-purple-300">
                {groupName ?? sub.conversationId}
              </span>
            )}
            {sub.ticketAlerts && (
              <span className="px-2 py-0.5 text-xs rounded bg-blue-900/50 text-blue-300">
                Tickets: {sub.ticketAlerts}
              </span>
            )}
          </div>
          {sub.location && (
            <div className="flex items-center gap-1 text-gray-400 text-xs">
              <MapPin size={12} />
              <span>{sub.location}</span>
              {sub.radiusKm && <span>· {sub.radiusKm}km</span>}
            </div>
          )}
        </div>
        <div className="text-right text-xs text-gray-500 shrink-0">
          {sub.waitingEventIds && sub.waitingEventIds.length > 0 && (
            <div className="text-amber-400">
              {sub.waitingEventIds.length} pending events
            </div>
          )}
          {sub.lastAggregatedPushAt && (
            <div>Last push: {formatDate(sub.lastAggregatedPushAt)}</div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex flex-wrap gap-1 flex-1">
          {sub.categories.map((cat) => (
            <span
              key={cat}
              className="px-2 py-0.5 text-xs rounded-full bg-gray-700 text-gray-300"
            >
              {cat}
            </span>
          ))}
        </div>
        <button
          type="button"
          onClick={() => toggleEditing("categories")}
          className="text-gray-500 hover:text-gray-300"
        >
          {editing === "categories" ? <X size={14} /> : <Pencil size={14} />}
        </button>
      </div>

      {editing === "categories" && (
        <CategoryEditor
          categories={sub.categories}
          tone="positive"
          onSave={(cats) => {
            onUpdate(sub.id, { categories: cats });
            setEditing(null);
          }}
        />
      )}

      <div className="flex items-center gap-2">
        <div className="flex flex-wrap gap-1 flex-1">
          {sub.blacklistedCategories && sub.blacklistedCategories.length > 0
            ? sub.blacklistedCategories.map((cat) => (
              <span
                key={cat}
                className="px-2 py-0.5 text-xs rounded-full bg-red-900/40 text-red-300 border border-red-800/60"
              >
                {cat}
              </span>
            ))
            : (
              <span className="text-xs text-gray-600">
                No blocked categories
              </span>
            )}
        </div>
        <button
          type="button"
          onClick={() => toggleEditing("blockedCategories")}
          className="text-gray-500 hover:text-gray-300"
        >
          {editing === "blockedCategories"
            ? <X size={14} />
            : <Pencil size={14} />}
        </button>
      </div>

      {editing === "blockedCategories" && (
        <CategoryEditor
          categories={sub.blacklistedCategories ?? []}
          tone="negative"
          onSave={(cats) => {
            onUpdate(sub.id, { blacklistedCategories: cats });
            setEditing(null);
          }}
        />
      )}

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400">
        <span>{scheduleLabel(sub)}</span>
        <div className="flex items-center gap-1">
          {sub.blacklistedWords && sub.blacklistedWords.length > 0
            ? (
              <span className="text-red-400/80">
                Blocked: {sub.blacklistedWords.join(", ")}
              </span>
            )
            : <span className="text-gray-600">No blocked words</span>}
          <button
            type="button"
            onClick={() => toggleEditing("blacklist")}
            className="text-gray-500 hover:text-gray-300"
          >
            {editing === "blacklist" ? <X size={12} /> : <Pencil size={12} />}
          </button>
        </div>
        {sub.whitelistedWords && sub.whitelistedWords.length > 0 && (
          <span className="text-emerald-400/80">
            Keywords: {sub.whitelistedWords.join(", ")}
          </span>
        )}
      </div>

      {editing === "blacklist" && (
        <BlacklistEditor
          words={sub.blacklistedWords ?? []}
          onSave={(words) => {
            onUpdate(sub.id, { blacklistedWords: words });
            setEditing(null);
          }}
        />
      )}
    </div>
  );
};

const matchesSubType = (sub: Sub, subType: SubType) =>
  subType === ""
    ? true
    : subType === "group"
    ? !!sub.conversationId
    : !sub.conversationId;

export const AdminSubscriptions = () => {
  const [country, setCountry] = useState<Country | "">("");
  const [subType, setSubType] = useState<SubType>("");

  const subsQuery = db.useQuery({
    subscriptions: {
      $: {
        ...(country ? { where: { country } } : {}),
      },
    },
    whatsappGroups: {},
  });

  if (subsQuery.isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }
  if (subsQuery.error) {
    return <div className="text-red-400 p-4">{subsQuery.error.message}</div>;
  }

  const { subscriptions, whatsappGroups } = subsQuery.data;

  const groupNameMap: Record<string, string> = Object.fromEntries(
    whatsappGroups
      .filter((g) => g.name)
      .map((g) => [g.whatsappId, g.name!]),
  );

  const filtered = subType
    ? subscriptions.filter((s) => matchesSubType(s, subType))
    : subscriptions;

  const handleUpdate = (id: string, fields: Record<string, unknown>) =>
    db.transact(tx.subscriptions[id].update(fields));

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-gray-400" />
          <Select
            size="sm"
            value={country}
            onChange={(e) =>
              setCountry(
                (e as unknown as { target: { value: string } }).target
                  .value as Country | "",
              )}
            options={countries}
          />
          <Select
            size="sm"
            value={subType}
            onChange={(e) =>
              setSubType(
                (e as unknown as { target: { value: string } }).target
                  .value as SubType,
              )}
            options={subTypes}
          />
        </div>
        <span className="text-gray-500 text-sm ml-auto">
          {filtered.length} shown
        </span>
      </div>

      {filtered.length === 0
        ? (
          <div className="text-center text-gray-500 py-12">
            No subscriptions found
          </div>
        )
        : (
          <div className="space-y-3">
            {filtered.map((sub) => (
              <SubscriptionCard
                key={sub.id}
                sub={sub}
                groupName={sub.conversationId
                  ? groupNameMap[sub.conversationId]
                  : undefined}
                onUpdate={handleUpdate}
              />
            ))}
          </div>
        )}
    </div>
  );
};
